import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Marker, MarkerClusterer } from '@react-google-maps/api';
import SafeGoogleMap from './components/SafeGoogleMap';
import MapPermitCallout, { buildPermitCalloutContent } from './components/MapPermitCallout';
import PermitEditPanel from './components/PermitEditPanel';
import { useRobinhoodGoogleMaps } from './hooks/useRobinhoodGoogleMaps';
import MapsUnavailable from './components/MapsUnavailable';
import { getGoogleMapsKeyIssue } from './api/mapsConfig';
import {
  getPermitMapPosition,
  permitsWithValidCoordinates,
  isGoogleMapsReady,
  validateCoordinates,
} from './utils/mapCoordinates';
import MapLocationSummary from './components/MapLocationSummary';
import PageShell from './components/PageShell';
import { fetchPermits, updatePermitRecord } from './api/govopsPermitService';
import { buildUpdatePermitPayload } from './utils/permitFields';
import { getPermitMarkerIcon } from './utils/mapMarkers';
import { formatGovOpsError, isGovOpsAuthError } from './api/govopsErrors';

const containerStyle = {
  width: '100%',
  height: '80vh',
};

const center = { lat: 32.7157, lng: -117.1611 };

const emptySelection = {
  id: null,
  type: '',
  description: '',
  coordinates: { latitude: '', longitude: '' },
  address: '',
};

const PermitListPanel = ({ permits, onSelect, selectedId }) => {
  const mapped = permitsWithValidCoordinates(permits);
  const withoutCoords = permits.filter((p) => !getPermitMapPosition(p));

  return (
    <section className="rhs-map-permit-list" aria-label="Permits">
      <h3 className="rhs-card-title">Permits ({permits.length})</h3>
      {mapped.length > 0 && (
        <ul className="rhs-list mb-3">
          {mapped.map(({ permit, position }) => (
            <li key={permit.id}>
              <button
                type="button"
                className={`rhs-link-btn${selectedId === permit.id ? ' rhs-link-btn--active' : ''}`}
                onClick={() => onSelect(permit)}
              >
                {permit.type || 'Permit'} — {permit.description || permit.id}{' '}
                <span className="rhs-muted">
                  ({position.lat.toFixed(5)}, {position.lng.toFixed(5)})
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {withoutCoords.length > 0 && (
        <p className="rhs-muted text-sm">
          {withoutCoords.length} permit(s) without valid coordinates — edit lat/lng below to show on map.
        </p>
      )}
      {permits.length === 0 && <p className="rhs-muted">No permits yet. Submit one from the dashboard or permit form.</p>}
    </section>
  );
};

function googleMapsDirectionsUrl(origin, destination) {
  const o = `${origin.lat},${origin.lng}`;
  const d = `${destination.lat},${destination.lng}`;
  return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(o)}&destination=${encodeURIComponent(d)}&travelmode=driving`;
}

const PermitMapCanvas = ({
  permits,
  selectedPermit,
  setSelectedPermit,
  calloutDismissed,
  setCalloutDismissed,
}) => {
  const [map, setMap] = useState(null);
  const mappedPermits = useMemo(() => permitsWithValidCoordinates(permits), [permits]);

  const onLoad = useCallback((mapInstance) => setMap(mapInstance), []);
  const onUnmount = useCallback(() => setMap(null), []);

  const selectedPosition = useMemo(
    () => getPermitMapPosition(selectedPermit),
    [selectedPermit]
  );

  const callout = useMemo(
    () => buildPermitCalloutContent(selectedPermit),
    [selectedPermit]
  );

  const showCallout = Boolean(
    selectedPermit?.id && selectedPosition && !calloutDismissed
  );

  useEffect(() => {
    if (!map || !selectedPosition) return;
    map.panTo(selectedPosition);
  }, [map, selectedPosition]);

  const handleMapClick = useCallback(
    (e) => {
      if (!selectedPermit?.id || !e?.latLng) return;
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setSelectedPermit((prev) => ({
        ...prev,
        coordinates: { latitude: lat, longitude: lng },
      }));
      map?.panTo({ lat, lng });
    },
    [map, selectedPermit?.id, setSelectedPermit]
  );

  const directionsHref = useMemo(() => {
    if (!selectedPosition) return null;
    return googleMapsDirectionsUrl(center, selectedPosition);
  }, [selectedPosition]);

  return (
    <>
      <p className="rhs-muted text-sm mb-2">
        Click a marker to view details. With a permit selected, click the map to set coordinates (then Save).
      </p>

      <SafeGoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={12}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onClick={handleMapClick}
        options={{ streetViewControl: false }}
      >
        <MarkerClusterer>
          {(clusterer) =>
            mappedPermits.map(({ permit, position }) => {
              const selected = selectedPermit?.id === permit.id;
              return (
                <Marker
                  key={permit.id}
                  position={position}
                  title={permit.type}
                  clusterer={clusterer}
                  icon={getPermitMarkerIcon(permit.status, { selected })}
                  onClick={() => {
                    setCalloutDismissed(false);
                    setSelectedPermit(permit);
                  }}
                />
              );
            })
          }
        </MarkerClusterer>
        {showCallout && (
          <MapPermitCallout
            position={selectedPosition}
            title={callout.title}
            lines={callout.lines}
            onClose={() => setCalloutDismissed(true)}
          />
        )}
      </SafeGoogleMap>

      {directionsHref && (
        <p className="rhs-muted text-sm mt-2">
          <a href={directionsHref} target="_blank" rel="noopener noreferrer" className="rhs-quick-nav-item">
            Open driving directions in Google Maps
          </a>
        </p>
      )}
    </>
  );
};

const PermitMap = () => {
  const location = useLocation();
  const [permits, setPermits] = useState([]);
  const [selectedPermit, setSelectedPermit] = useState(emptySelection);
  const [calloutDismissed, setCalloutDismissed] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [permitsError, setPermitsError] = useState('');
  const [loadingPermits, setLoadingPermits] = useState(true);

  const { isLoaded, loadError } = useRobinhoodGoogleMaps();
  const keyIssue = getGoogleMapsKeyIssue();
  const mapsReady = !keyIssue && !loadError && isLoaded && isGoogleMapsReady();

  const loadPermits = useCallback(async () => {
    try {
      const permitsData = await fetchPermits();
      setPermits(permitsData);
      setPermitsError('');
    } catch (err) {
      if (!isGovOpsAuthError(err)) console.error('Failed to load permits:', err);
      setPermitsError(formatGovOpsError(err));
    } finally {
      setLoadingPermits(false);
    }
  }, []);

  useEffect(() => {
    loadPermits();
    const onFocus = () => loadPermits();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadPermits]);

  useEffect(() => {
    const permitId = location.state?.permitId;
    if (!permitId || !permits.length) return;
    const match = permits.find((p) => p.id === permitId);
    if (match) {
      setSelectedPermit(match);
      setCalloutDismissed(false);
    }
  }, [location.state?.permitId, permits]);

  const handleSave = async () => {
    if (!selectedPermit?.id) return;
    setSaving(true);
    setSaveSuccess(false);
    setSaveError('');
    try {
      const coords = selectedPermit.coordinates || {};
      const coordCheck = validateCoordinates(
        coords.latitude ?? coords.lat,
        coords.longitude ?? coords.lng
      );

      const normalizedCoords = coordCheck.ok
        ? { latitude: coordCheck.lat, longitude: coordCheck.lng }
        : null;

      const patchBody = buildUpdatePermitPayload({
        ...selectedPermit,
        ...(normalizedCoords ? { coordinates: normalizedCoords } : {}),
      });
      const updated = await updatePermitRecord(selectedPermit.id, patchBody);

      const permitsData = await fetchPermits();
      setPermits(permitsData);
      const fresh = permitsData.find((p) => p.id === updated.id) || updated;
      setSelectedPermit(fresh);
      setSaveSuccess(true);
      setCalloutDismissed(false);
      setTimeout(() => setSaveSuccess(false), 4000);
    } catch (err) {
      console.error('Error updating permit:', err);
      setSaveError(formatGovOpsError(err));
    } finally {
      setSaving(false);
    }
  };

  const selectedCallout = useMemo(
    () => buildPermitCalloutContent(selectedPermit),
    [selectedPermit]
  );

  const selectedPosition = useMemo(
    () => getPermitMapPosition(selectedPermit),
    [selectedPermit]
  );

  const handleSelectPermit = useCallback((permit) => {
    setSelectedPermit(permit);
  }, []);

  const handleCancelEdit = () => {
    setSelectedPermit(emptySelection);
  };

  const mapStatusMessage = (() => {
    if (keyIssue || loadError) return null;
    if (!isLoaded) return 'Loading map…';
    if (!isGoogleMapsReady()) {
      return 'Map loading or unavailable. Permits below still load from GovOps.';
    }
    return null;
  })();

  return (
    <PageShell
      title="Permit map"
      subtitle="All permits submitted via dashboard or permit forms appear here when they have valid coordinates."
    >
      <nav className="rhs-quick-nav" aria-label="Submit permits">
        <Link to="/dashboard" className="rhs-quick-nav-item">
          Dashboard submit
        </Link>
        <Link to="/permit/submit" className="rhs-quick-nav-item">
          Address submit
        </Link>
        <Link to="/permit" className="rhs-quick-nav-item">
          Full permit form
        </Link>
      </nav>

      {permitsError && (
        <p className="rhs-error" role="alert">
          {permitsError}
        </p>
      )}

      {loadingPermits && (
        <p className="rhs-muted" role="status">
          Loading permits…
        </p>
      )}

      {keyIssue && <MapsUnavailable />}
      {!keyIssue && loadError && <MapsUnavailable loadError={loadError} />}
      {!keyIssue && !loadError && mapStatusMessage && (
        <p className="rhs-muted" role="status">
          {mapStatusMessage}
        </p>
      )}

      {mapsReady ? (
        <PermitMapCanvas
          permits={permits}
          selectedPermit={selectedPermit}
          setSelectedPermit={setSelectedPermit}
          calloutDismissed={calloutDismissed}
          setCalloutDismissed={setCalloutDismissed}
        />
      ) : (
        !keyIssue &&
        !loadError && (
          <PermitListPanel
            permits={permits}
            onSelect={handleSelectPermit}
            selectedId={selectedPermit.id}
          />
        )
      )}

      {mapsReady && (
        <PermitListPanel
          permits={permits}
          onSelect={handleSelectPermit}
          selectedId={selectedPermit.id}
        />
      )}

      {saveError && (
        <p className="rhs-error" role="alert">
          {saveError}
        </p>
      )}

      {selectedPermit?.id && (
        <>
          <PermitEditPanel
            permit={selectedPermit}
            onChange={setSelectedPermit}
            onSave={handleSave}
            onCancel={handleCancelEdit}
            saving={saving}
            saveSuccess={saveSuccess}
          />
          {saveSuccess && selectedPosition && (
            <MapLocationSummary
              position={selectedPosition}
              title={`Saved — ${selectedCallout.title}`}
              lines={selectedCallout.lines}
            />
          )}
          {!saveSuccess && selectedPosition && (
            <p className="rhs-muted text-sm">
              Permit details are shown on the map above the marker. Save to persist changes to GovOps.
            </p>
          )}
          {selectedPermit?.id && !selectedPosition && (
            <p className="rhs-muted text-sm" role="status">
              Add valid latitude and longitude, then save to show this permit on the map.
            </p>
          )}
        </>
      )}
    </PageShell>
  );
};

export default React.memo(PermitMap);
